import React, { useState, useEffect } from "react";
import ContactVendorleadsModal from "../components/ContactVendorleadsModal";
import SignInModal from "../components/SignInModal";
import Navbar from "../components/Navbar";
import FAQ from "../components/FAQ";
import TestimonialCarousel from "../components/TestimonialCarousel";

const BRAND_COLOR = "#1a3f6b";
const BRAND_ACCENT_COLOR = "#10b981";
const GRAY_DARK_COLOR = "#374151";
const WHITE_COLOR = "#fff";

const FREE_FEATURES = [
  "Download report page",
  "Live valuation page",
  "Users can manage valuations",
  "Branded suburb profiles",
  "Comparable finder tool",
  "5 activity notifications per month",
];

const PAID_FEATURES = [
  "Unlimited activity notifications",
  "Monthly branded property updates to all contacts",
  "Monthly branded suburb updates to all contacts",
  "CRM integration",
  "Customise branding",
  "AI news articles",
  "AI Co-Pilot and outbound triggers",
  "Vendorleads platform for detailed activity monitoring",
  "Dedicated support",
];

const FAQ_ITEMS = [
  {
    question: "How long does it take to get set up?",
    answer:
      "Most agents are fully set up within 24 hours. Simply connect your contacts, add your branding, and choose your target suburbs. Our onboarding wizard walks you through every step.",
  },
  {
    question: "How does the AI generate property reports?",
    answer:
      "Our AI analyses recently sold and listed properties near each contact's address, performs comparable property analysis, and generates dynamic sales and rental estimates. Reports are refreshed monthly and sent automatically as fully branded emails.",
  },
  {
    question: "Can I customise the branding on reports, articles, and emails?",
    answer:
      "Absolutely. Every touchpoint carries your profile photo, agency logo, brand colours, and contact details. Your clients will see your brand front and centre on every report, article, and email.",
  },
  {
    question: "What happens when a contact replies to an AI-sent email?",
    answer:
      "Replies land in your dashboard where the AI drafts a suggested response. You can accept it with one click or write your own. Nothing is ever sent without your approval, so you always stay in control of the conversation.",
  },
  {
    question: "Is there a lock-in contract?",
    answer:
      "No. Vendorleads is month-to-month with no lock-in contracts. You can upgrade, downgrade, or cancel your plan at any time from your account settings.",
  },
];

export default function Pricing() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showContact, setShowContact] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div style={{ background: WHITE_COLOR, minHeight: "100vh" }}>
      <Navbar onSignIn={() => setShowSignIn(true)} />

      {/* Hero */}
      <header
        style={{
          background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #134e4a 100%)",
          padding: isMobile ? "6rem 1.5rem 3.5rem" : "8rem 2rem 5rem",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            pointerEvents: "none",
          }}
        />
        <div style={{ maxWidth: "700px", margin: "0 auto", position: "relative", zIndex: 1 }}>
          <h1
            style={{
              fontSize: isMobile ? "2rem" : "3rem",
              fontWeight: 800,
              color: WHITE_COLOR,
              marginBottom: "1rem",
              lineHeight: 1.15,
              letterSpacing: "-0.02em",
            }}
          >
            Plans that grow with you
          </h1>
          <p
            style={{
              color: "rgba(255,255,255,0.7)",
              fontSize: isMobile ? "1rem" : "1.15rem",
              lineHeight: 1.7,
              maxWidth: "560px",
              margin: "0 auto",
            }}
          >
            Start free and see instant results. When you're ready, unlock a fully autonomous AI prospecting autopilot.
          </p>
        </div>
      </header>

      {/* Pricing Cards */}
      <section
        style={{
          padding: isMobile ? "3rem 1.5rem" : "4rem 2rem",
          background: "#f8fafc",
        }}
      >
        <div style={{ maxWidth: "950px", margin: "0 auto" }}>

          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              gap: "1.5rem",
              alignItems: "stretch",
            }}
          >
            {/* Free Plan */}
            <div
              style={{
                flex: 1,
                background: WHITE_COLOR,
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                padding: isMobile ? "2rem 1.5rem" : "2.5rem 2rem",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  display: "inline-block",
                  background: "rgba(100,116,139,0.1)",
                  color: "#64748b",
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  borderRadius: "6px",
                  padding: "0.3rem 0.75rem",
                  marginBottom: "1rem",
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  width: "fit-content",
                }}
              >
                Free
              </div>
              <div style={{ marginBottom: "0.5rem" }}>
                <span style={{ fontSize: "2.5rem", fontWeight: 800, color: BRAND_COLOR }}>$0</span>
                <span style={{ color: GRAY_DARK_COLOR, fontSize: "0.95rem" }}> /month</span>
              </div>
              <p style={{ color: GRAY_DARK_COLOR, fontSize: "0.92rem", lineHeight: 1.6, marginBottom: "1.5rem", opacity: 0.7 }}>
                Everything you need to get started and offer value to homeowners.
              </p>
              <ul style={{ listStyle: "none", padding: 0, margin: "0 0 2rem 0", display: "flex", flexDirection: "column", gap: "0.7rem", flex: 1 }}>
                {FREE_FEATURES.map((f, i) => (
                  <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "0.6rem", fontSize: "0.92rem", color: GRAY_DARK_COLOR, lineHeight: 1.5 }}>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        background: "rgba(16,185,129,0.12)",
                        color: BRAND_ACCENT_COLOR,
                        fontSize: "0.7rem",
                        flexShrink: 0,
                        marginTop: "2px",
                      }}
                    >
                      ✓
                    </span>
                    {f}
                  </li>
                ))}
              </ul>

<button
                style={{
                  border: `1px solid ${BRAND_ACCENT_COLOR}`,
                  borderRadius: "8px",
                  padding: "0.75rem 1.5rem",
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  background: "transparent",
                  color: BRAND_ACCENT_COLOR,
                  transition: "background 0.15s, color 0.15s",
                  width: "100%",
                }}
                onClick={() => setShowSignIn(true)}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = BRAND_ACCENT_COLOR;
                  e.currentTarget.style.color = WHITE_COLOR;
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = BRAND_ACCENT_COLOR;
                }}
              >
                Get Started Free
              </button>
            </div>

            {/* Growth Plan */}
            <div
              style={{
                flex: 1,
                background: WHITE_COLOR,
                borderRadius: "16px",
                border: `2px solid ${BRAND_ACCENT_COLOR}`,
                padding: isMobile ? "2rem 1.5rem" : "2.5rem 2rem",
                display: "flex",
                flexDirection: "column",
                position: "relative",
                boxShadow: "0 8px 30px rgba(16,185,129,0.12)",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "-12px",
                  right: "1.5rem",
                  background: BRAND_ACCENT_COLOR,
                  color: WHITE_COLOR,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  borderRadius: "6px",
                  padding: "0.3rem 0.75rem",
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                }}
              >
                Full Platform
              </div>
              <div
                style={{
                  display: "inline-block",
                  background: "rgba(16,185,129,0.1)",
                  color: BRAND_ACCENT_COLOR,
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  borderRadius: "6px",
                  padding: "0.3rem 0.75rem",
                  marginBottom: "1rem",
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  width: "fit-content",
                }}
              >
                Growth
              </div>
              <div style={{ marginBottom: "0.25rem" }}>
                <span style={{ fontSize: "1.8rem", fontWeight: 800, color: BRAND_COLOR }}>Custom pricing</span>
              </div>
              <p style={{ color: GRAY_DARK_COLOR, fontSize: "0.78rem", marginBottom: "1.5rem", opacity: 0.5 }}>
                Tailored to your database size. Log in to see pricing.
              </p>
              <ul style={{ listStyle: "none", padding: 0, margin: "0 0 2rem 0", display: "flex", flexDirection: "column", gap: "0.7rem", flex: 1 }}>
                <li style={{ fontSize: "0.85rem", color: GRAY_DARK_COLOR, fontWeight: 600, marginBottom: "0.25rem", opacity: 0.5, textTransform: "uppercase", letterSpacing: "0.03em" }}>
                  Everything in Free, plus:
                </li>
                {PAID_FEATURES.map((f, i) => (
                  <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "0.6rem", fontSize: "0.92rem", color: GRAY_DARK_COLOR, lineHeight: 1.5 }}>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        background: "rgba(16,185,129,0.12)",
                        color: BRAND_ACCENT_COLOR,
                        fontSize: "0.7rem",
                        flexShrink: 0,
                        marginTop: "2px",
                      }}
                    >
                      ✓
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
              <button
                style={{
                  border: "none",
                  borderRadius: "8px",
                  padding: "0.75rem 1.5rem",
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  background: BRAND_ACCENT_COLOR,
                  color: WHITE_COLOR,
                  boxShadow: "0 4px 14px rgba(16,185,129,0.4)",
                  transition: "transform 0.15s, box-shadow 0.15s",
                  width: "100%",
                }}
                onClick={() => setShowSignIn(true)}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = "translateY(-1px)";
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(16,185,129,0.5)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 4px 14px rgba(16,185,129,0.4)";
                }}
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials carousel */}
      <TestimonialCarousel isMobile={isMobile} />

      {/* FAQ */}
      <FAQ isMobile={isMobile} items={FAQ_ITEMS} />

      {/* CTA Section */}
      <section
        style={{
          background: WHITE_COLOR,
          padding: isMobile ? "3.5rem 1.5rem" : "5rem 2rem",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: "600px", margin: "0 auto" }}>
          <h2
            style={{
              fontSize: isMobile ? "1.8rem" : "2.4rem",
              fontWeight: 800,
              color: BRAND_COLOR,
              marginBottom: "1rem",
              lineHeight: 1.2,
              letterSpacing: "-0.02em",
            }}
          >
            Ready to put your prospecting on autopilot?
          </h2>
          <p
            style={{
              color: GRAY_DARK_COLOR,
              marginBottom: "2rem",
              fontSize: "1.1rem",
              lineHeight: 1.6,
            }}
          >
            Set it up in minutes, then let AI handle everything — reports, articles, campaigns, and lead nurturing — so you never have to think about it again.
          </p>
          <button
            style={{
              border: "none",
              borderRadius: "8px",
              padding: "0.9rem 2.5rem",
              fontSize: "1.05rem",
              fontWeight: 600,
              cursor: "pointer",
              background: BRAND_ACCENT_COLOR,
              color: WHITE_COLOR,
              boxShadow: "0 4px 14px rgba(16,185,129,0.4)",
              transition: "transform 0.15s, box-shadow 0.15s",
            }}
            onClick={() => setShowSignIn(true)}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow = "0 6px 20px rgba(16,185,129,0.5)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 14px rgba(16,185,129,0.4)";
            }}
          >
            Get Started
          </button>
        </div>
      </section>

      {showSignIn && <SignInModal show={showSignIn} onHide={() => setShowSignIn(false)} />}
      {showContact && <ContactVendorleadsModal show={showContact} onHide={() => setShowContact(false)} />}
    </div>
  );
}
